import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExternalLink,
  Eye,
  X,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import projects from "../data/projects";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const item = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.96,
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section
      id="projects"
      className="relative overflow-hidden py-16 md:pb-20 bg-gradient-to-b from-white via-gray-50 to-purple-50"
    >
      {/* ================= Background ================= */}

      <motion.div
        animate={{
          x: [0, 80, -20, 0],
          y: [0, -20, 20, 0],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute -left-32 top-0 h-80 w-80 rounded-full bg-purple-300/25 blur-[120px]"
      />

      <motion.div
        animate={{
          x: [0, -50, 20, 0],
          y: [0, 30, -20, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute right-0 bottom-0 h-96 w-96 rounded-full bg-indigo-300/25 blur-[130px]"
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-6">

        {/* ================= Heading ================= */}

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >

          <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-100 px-4 py-2">

          <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" />

            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-4 sm:py-2 rounded-full bg-white/80 backdrop-blur-xl border border-purple-200 shadow-lg text-[11px] sm:text-sm font-semibold text-purple-700">
              Portfolio
            </span>

          </div>
          <h2 className="mt-2 sm:mt-6 text-2xl sm:text-4xl md:text-5xl font-black leading-tight">
            <span className="text-gray-900">
              Our
            </span>
            <span className="bg-gradient-to-r from-fuchsia-600 ms-2 via-purple-600 to-pink-500 bg-clip-text text-transparent">
              Projects
            </span>
          </h2>
          <p className="mx-auto mt-3 sm:mt-5 max-w-2xl text-[13px] sm:text-base md:text-lg leading-6 sm:leading-8 text-gray-600">

            We build premium websites, AI solutions,
            mobile apps and digital products that
            help businesses scale faster.

          </p>
             {/* Divider */}
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: 100 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mx-auto mt-5 sm:mt-8 h-1 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 sm:w-32"
        />

        </motion.div>

        {/* ================= Grid ================= */}

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{
            once: true,
            amount: .2,
          }}
        className="mt-8 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-7"
        >

          {projects.map((project, index) => (

            <motion.div
              key={index}
              variants={item}
              whileHover={{
                y: -12,
              }}
              transition={{
                type: "spring",
                stiffness: 220,
              }}
              className="group relative"
            >

              {/* Gradient Border */}

              <div className="rounded-[22px] sm:rounded-[30px] bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-[2px]">

                {/* Card */}

                <div className="overflow-hidden rounded-[20px] sm:rounded-[28px]  bg-white shadow-xl transition-all duration-500 group-hover:shadow-[0_35px_70px_rgba(124,58,237,.25)]">

                  {/* Image */}

                  <div className="relative overflow-hidden">

                    <img
                      src={project.img}
                      alt={project.title}
                      className="aspect-[16/10] w-full object-cover transition duration-700 group-hover:scale-110"
                    />

                    {/* Overlay */}

                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent opacity-0 group-hover:opacity-100 transition duration-500 flex flex-col justify-end p-4 sm:p-6">

                    <span className="inline-flex w-fit rounded-full bg-white/20 px-2.5 py-1 text-[10px] sm:text-xs font-semibold text-white backdrop-blur-md">

                        Featured Project

                      </span>

                      <h3 className="mt-3 text-lg sm:text-2xl font-bold text-white">

                        {project.title}

                      </h3>

                      <div className="mt-4 sm:mt-6 flex flex-wrap gap-2 sm:gap-3">

                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-3 py-2 sm:px-5 sm:py-3 text-xs sm:text-sm font-semibold text-white"
                        >

                        <ExternalLink size={16} className="sm:w-[18px] sm:h-[18px]" /> 

                          Live Demo

                        </a>

                        <button
                          onClick={() => setSelectedProject(project)}
                         className="inline-flex items-center gap-1.5 rounded-xl border border-white/30 bg-white/10 px-3 py-2 sm:px-5 sm:py-3 text-xs sm:text-sm font-semibold text-white"
                        >
                        <Eye size={16} className="sm:w-[18px] sm:h-[18px]" />

                          View Details

                        </button>

                      </div>

                    </div>

                  </div>

                  {/* Bottom */}

                  <div className="flex items-center justify-between p-4 sm:p-6">

                    <div>

                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">

                        {project.title}

                      </h3>

                      <p className="mt-1 text-xs sm:text-sm text-gray-500">

                        Modern & Responsive Website

                      </p>

                    </div>

                    <button
                      onClick={() => setSelectedProject(project)}
                      className="rounded-full bg-purple-100 p-2 sm:p-3  text-purple-600 transition hover:bg-purple-600 hover:text-white"
                    >
                    <ArrowUpRight size={16} className="sm:w-5 sm:h-5" />
                    </button>

                  </div>

                </div>

              </div>

            </motion.div>

          ))}

        </motion.div>
                {/* ================= Popup ================= */}

                <AnimatePresence>
          {selectedProject && (
            <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-black/70 backdrop-blur-md px-4 py-10 md:py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            >
              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.9,
                  y: 50,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.9,
                  y: 50,
                }}
                transition={{
                  duration: 0.35,
                }}
                onClick={(e) => e.stopPropagation()}
              className="relative my-8 w-full max-w-md md:max-w-xl lg:max-w-2xl overflow-hidden rounded-[30px] bg-white shadow-[0_35px_80px_rgba(0,0,0,.30)]"
              >
                {/* Close */}

                <button
                onClick={() => setSelectedProject(null)}
                className="absolute right-3 top-3 sm:right-5 sm:top-5 z-20 w-8 h-8 flex sm:h-9 sm:w-9 items-center justify-center rounded-full bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:rotate-90"
              >
                <X size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>

                {/* Image */}

                <div className="relative">

                  <img
                    src={selectedProject.img}
                    alt={selectedProject.title}
                    className="h-55 md:h-64 lg:h-72 w-full object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                </div>

                {/* Content */}

                <div className="p-4 sm:p-6">

                  <h3 className="text-xl sm:text-2xl font-black text-gray-900">

                    {selectedProject.title}

                  </h3>

                  <p className="mt-3 text-[13px] sm:text-base leading-6 sm:leading-7 text-gray-600">

                    {selectedProject.description}

                  </p>

                  {/* Buttons */}

                  <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">

                    <a
                      href={selectedProject.link}
                      target="_blank"
                      rel="noopener noreferrer"
                     className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 px-4 py-2 text-sm font-semibold text-white"
                    >
                      <ExternalLink size={15} />

                      Visit Website
                    </a>

                    <button
                      onClick={() => setSelectedProject(null)}
                    className="flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                    >
                      Close
                    </button>

                  </div>

                </div>

              </motion.div>

            </motion.div>
          )}
        </AnimatePresence>

      </div>

    </section>
  );
}